import { Fragment, useEffect, useContext } from "react";
import {
  getAdjustedDate,
  getFormattedDate,
} from "../../../../../../legacyComponents/util/helperFunctions";
import { formatDate } from "../../../../../../legacyComponents/util/formating";
import { options } from "../util";
import { Stack, Typography } from "@mui/material";
import { Field } from "formik";
import { FormContainer, NavButtons } from "../../../../Menu/Flow";
import { UserContext } from "../../../../../../context/UserContext.jsx";
import employerFlowStyles from "../../styles";

export default function Dates(props) {
  const {
    mode,
    exitUrl,
    step,
    formik,
    onboarding,
    displayOnly,
    employerPlanId,
    dateOptions,
  } = props;
  const { values, errors, touched, setFieldValue } = formik;

  const { isSeanOrCustomer, getEmployerPlan } = useContext(UserContext);
  const employerPlan = getEmployerPlan(employerPlanId);
  const planType = employerPlan?.type || "";
  const isICHRA = planType === "ICHRA";
  const hasPreviousPlan = Boolean(employerPlan?.previousPlanId);

  // `isSeanOrCustomer` is true for ordinary users; the override is for the
  // one account where it is false. Aliased so the rules below read forwards.
  const canOverrideDates = !isSeanOrCustomer;

  // Start date locks on a rollover: it is inherited from the previous year.
  const startDateDisabled =
    displayOnly || (hasPreviousPlan && !canOverrideDates);

  // End date locks on a rollover (inherited) and on ICHRA (always Dec 31).
  const endDateDisabled =
    !values?.startDate?.dateString ||
    displayOnly ||
    (!canOverrideDates && (hasPreviousPlan || isICHRA));

  // Same value as before: ICHRA shows only December unless overriding.
  const lockEndDateToDecember = isICHRA && !canOverrideDates;

  // Handle both string value ("0", "1", "") and legacy object format
  const midYearPlanValue =
    typeof values?.midYearPlan === "object" && values?.midYearPlan !== null
      ? values.midYearPlan.value
      : values?.midYearPlan === "1"
        ? true
        : values?.midYearPlan === "0"
          ? false
          : null;
  const midYearPlan = midYearPlanValue === true;

  // Helper to format date as "Month Year"
  const formatMonthYear = (date) => {
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
  };

  // Get start date options - include existing date if not in list
  const getStartDateOptions = () => {
    const baseOptions = midYearPlan
      ? options.midPlanYearStartDates()
      : options.startDates(isSeanOrCustomer);

    // If there's an existing start date that's not in the options, include it first
    if (values.startDate?.dateString && values.startDate?.display) {
      const existingInList = baseOptions.some(
        (opt) => opt.dateString === values.startDate.dateString,
      );
      if (!existingInList) {
        return [values.startDate, ...baseOptions];
      }
    }

    return baseOptions;
  };

  const startDates = getStartDateOptions();

  // Calculate end date based on start date
  const calculateEndDate = (startDateString) => {
    const [year, month] = startDateString.split("-");
    const startYear = parseInt(year);
    const startMonth = parseInt(month);

    if (isICHRA) {
      // ICHRA: Dec 31 of same year as start date
      const endDateString = `${year}-12-31`;
      const endDateValue = new Date(startYear, 11, 31);

      return {
        display: formatMonthYear(endDateValue),
        value: endDateValue,
        dateString: endDateString,
      };
    } else {
      // Other plans: 11 months forward (end of 12th month)
      const endMonth = (startMonth + 11) % 12 || 12; // 1-12 range
      const endYear = startMonth + 11 > 12 ? startYear + 1 : startYear;
      const endDay = new Date(endYear, endMonth, 0).getDate(); // Last day of month

      const endDateString = `${endYear}-${String(endMonth).padStart(2, "0")}-${String(endDay).padStart(2, "0")}`;
      const endDateValue = new Date(endYear, endMonth - 1, endDay);

      return {
        display: formatMonthYear(endDateValue),
        value: endDateValue,
        dateString: endDateString,
      };
    }
  };

  // Auto-set end date
  useEffect(() => {
    if (!values.startDate?.dateString) {
      return;
    }

    // If endDate already exists with a valid dateString, keep it
    if (values.endDate?.dateString) {
      return;
    }

    // Only set end date if it's missing
    const endDate = calculateEndDate(values.startDate.dateString);
    setFieldValue("endDate", endDate);
  }, [
    values.startDate?.dateString,
    values.endDate?.dateString,
    isICHRA,
    setFieldValue,
  ]);

  // Get end date options - include existing date if not in list
  const getEndDateOptions = () => {
    if (!values?.startDate?.dateString) return [];

    let endOptions = [];

    if (lockEndDateToDecember) {
      // For ICHRA, only show Dec 31 of the start year
      const [year] = values.startDate.dateString.split("-");
      const endDateValue = new Date(parseInt(year), 11, 31);

      endOptions = [
        {
          display: formatMonthYear(endDateValue),
          value: endDateValue,
          dateString: `${year}-12-31`,
        },
      ];
    } else {
      // For non-ICHRA: Show options from 1 month after start to 11 months after start
      const [year, month] = values.startDate.dateString.split("-");
      const startYear = parseInt(year);
      const startMonth = parseInt(month);

      for (let i = 0; i <= 11; i++) {
        const optionMonth = (startMonth + i) % 12 || 12;
        const optionYear = startMonth + i > 12 ? startYear + 1 : startYear;
        const optionDay = new Date(optionYear, optionMonth, 0).getDate();

        const optionDateValue = new Date(
          optionYear,
          optionMonth - 1,
          optionDay,
        );
        const optionDateString = `${optionYear}-${String(optionMonth).padStart(2, "0")}-${String(optionDay).padStart(2, "0")}`;

        endOptions.push({
          display: formatMonthYear(optionDateValue),
          value: optionDateValue,
          dateString: optionDateString,
        });
      }
    }

    // If there's an existing end date that's not in the options, include it first
    if (values.endDate?.dateString && values.endDate?.display) {
      const existingInList = endOptions.some(
        (opt) => opt.dateString === values.endDate.dateString,
      );
      if (!existingInList) {
        endOptions = [values.endDate, ...endOptions];
      }
    }

    return endOptions;
  };

  const endDateOptions = getEndDateOptions();

  const handleStartDateChange = (e) => {
    const selectedIndex = e.target.selectedIndex - 1; // Account for placeholder option
    if (selectedIndex >= 0 && startDates[selectedIndex]) {
      const selectedStartDate = startDates[selectedIndex];
      setFieldValue("startDate", selectedStartDate);

      if (selectedStartDate?.dateString) {
        const endDate = calculateEndDate(selectedStartDate.dateString);
        setFieldValue("endDate", endDate);
      }
    }
  };

  const handleEndDateChange = (e) => {
    const selectedIndex = e.target.selectedIndex - 1; // Account for placeholder option
    if (selectedIndex >= 0 && endDateOptions[selectedIndex]) {
      setFieldValue("endDate", endDateOptions[selectedIndex]);
    }
  };
  return (
    <Stack spacing="30px" height="100%">
      <FormContainer>
        <Typography variant="h2" sx={employerFlowStyles.h2}>
          Plan Year
        </Typography>

        {/* Instructions */}
        {hasPreviousPlan && !canOverrideDates && (
          <Typography>
            This is not the initial plan year. Start and end dates are set
            automatically.
          </Typography>
        )}
        
        {!hasPreviousPlan && midYearPlan && (
          <Fragment>
            <Typography>
              Enter the start {!isICHRA && "and end "}date of your current{" "}
              {planType} plan year.
            </Typography>
            <Typography>
              Your {planType} with Salusion will have the same start and end
              dates.
            </Typography>
          </Fragment>
        )}

        {!hasPreviousPlan && !midYearPlan && (
          <Fragment>
            <Typography>
              Enter the start {!isICHRA && "and end "}date for this {planType}{" "}
              plan year.
            </Typography>
            {!isICHRA && (
              <Typography sx={{ marginTop: "20px" }}>
                The first plan year may be shorter than 12 months to align
                future years with your preferred start date.
              </Typography>
            )}
          </Fragment>
        )}

        <Stack spacing={2} sx={{ marginTop: "20px" }}>
          {/* Start Date Field */}
          <Stack>
            <Typography sx={{ marginBottom: "8px" }}>
              {planType} Start Month
            </Typography>
            <select
              name="startDate"
              value={values.startDate?.display || ""}
              onChange={handleStartDateChange}
              disabled={startDateDisabled}
              style={{
                width: "100%",
                padding: "12px",
                border: `1px solid ${errors.startDate && touched.startDate ? "#dc2626" : "#ACBEE8"}`,
                borderRadius: "8px",
                fontSize: "14px",
                backgroundColor: startDateDisabled ? "#f3f4f6" : "white",
                cursor: startDateDisabled ? "not-allowed" : "pointer",
              }}            >
              <option value="">Select Start Month</option>
              {startDates &&
                startDates.map((date, index) => (
                  <option key={index} value={date.display}>
                    {date.display}
                  </option>
                ))}
            </select>
            {errors.startDate && touched.startDate && (
              <div className="error-message-fields">{errors.startDate}</div>
            )}
          </Stack>

          {/* End Date Field */}
          <Stack>
            <Typography sx={{ marginTop: "20px", marginBottom: "8px" }}>
              {planType} End Month
            </Typography>
            <select
              name="endDate"
              value={values.endDate?.display || ""}
              onChange={handleEndDateChange}
              disabled={endDateDisabled}
              style={{
                width: "100%",
                padding: "12px",
                border: `1px solid ${errors.endDate && touched.endDate ? "#dc2626" : "#ACBEE8"}`,
                borderRadius: "8px",
                fontSize: "14px",
                backgroundColor: endDateDisabled ? "#f3f4f6" : "white",
                cursor: endDateDisabled ? "not-allowed" : "pointer",
              }}
            >
              <option value="">Select End Month</option>
              {endDateOptions &&
                endDateOptions.map((date, index) => (
                  <option key={index} value={date.display}>
                    {date.display}
                  </option>
                ))}
            </select>
            {errors.endDate && touched.endDate && (
              <div className="error-message-fields">{errors.endDate}</div>
            )}
          </Stack>
        </Stack>
      </FormContainer>

      <NavButtons mode={mode} enableSave={true} exitUrl={exitUrl} step={step} />
    </Stack>
  );
}
